import "../public/index.css";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import ReactPlayer from "react-player";
import {Contact} from "./Contact";

export function App() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);
  const [duration, setDuration] = useState(0);
  const [played, setPlayed] = useState(0); // 0..1
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [loadedSeconds, setLoadedSeconds] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [volume, setVolume] = useState(1); // 0..1
  const [isMuted, setIsMuted] = useState(false);
  const playerRef = useRef<any>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const videoElRef = useRef<HTMLVideoElement | null>(null);

  const onPlay = useCallback(() => {
    setHasStarted(true);
    setIsPlaying(true);
    setHasEnded(false);
  }, []);

  const togglePlay = useCallback(() => {
    setIsPlaying(p => !p);
    setHasEnded(false);
  }, []);

  const onEnded = useCallback(() => {
    setIsPlaying(false);
    setHasEnded(true);
  }, []);

  const getVideoFromElement = useCallback((el: Element): HTMLVideoElement | null => {
    // If it's already a video element, return it
    if (el.tagName === 'VIDEO') return el as HTMLVideoElement;
    // Check if it's a custom element (like hls-video) with a video inside
    const video = el.querySelector('video') || (el as any).shadowRoot?.querySelector('video');
    return video as HTMLVideoElement | null;
  }, []);

  const getVideo = useCallback((): HTMLVideoElement | null => {
    // Prefer cached ref
    if (videoElRef.current) return videoElRef.current;
    // Try react-player internal API
    const inst = playerRef.current;
    let internal: any = null;
    if (inst && typeof inst.getInternalPlayer === "function") {
      try {
        internal = inst.getInternalPlayer();
      } catch {
      }
      if (!internal) {
        try {
          internal = inst.getInternalPlayer("hls");
        } catch {
        }
      }
      if (!internal) {
        try {
          internal = inst.getInternalPlayer("file");
        } catch {
        }
      }
    }
    if (internal && typeof (internal as any).currentTime !== "undefined") {
      videoElRef.current = internal as HTMLVideoElement;
      return videoElRef.current;
    }
    // For HLS, react-player uses an hls-video custom element with a video inside or in shadowRoot
    const hlsElement = boxRef.current?.querySelector("hls-video");
    if (hlsElement) {
      const video = getVideoFromElement(hlsElement);
      if (video) {
        videoElRef.current = video;
        return videoElRef.current;
      }
    }
    // Fallback to DOM query within our box for standard video elements
    const el = boxRef.current?.querySelector("video") as HTMLVideoElement | null;
    if (el) videoElRef.current = el;
    return el;
  }, [getVideoFromElement]);

  const onReady = useCallback((player: any) => {
    // Clear cached ref so getVideo will re-query
    videoElRef.current = null;
    // Get the video element (works for both HLS custom elements and standard video)
    const video = player ? getVideoFromElement(player) : null;
    if (video) {
      videoElRef.current = video;
      if (isFinite(video.duration)) {
        setDuration(video.duration);
      }
      // Set initial volume
      video.volume = volume;
      video.muted = isMuted;
    } else {
      // Fallback to getVideo
      const vid = getVideo();
      if (vid) {
        if (isFinite(vid.duration)) {
          setDuration(vid.duration);
        }
        vid.volume = volume;
        vid.muted = isMuted;
      }
    }
  }, [getVideo, getVideoFromElement, volume, isMuted]);

  const seekTo = useCallback((fractionOrSeconds: number, type: "seconds" | "fraction" = "seconds") => {
    const video = getVideo();
    if (!video) {
      // Try react-player API as a last resort
      const inst = playerRef.current;
      if (inst && typeof inst.seekTo === "function") {
        inst.seekTo(fractionOrSeconds, type);
      }
      return;
    }
    const dur = isFinite(video.duration) && video.duration > 0 ? video.duration : (duration || 0);
    if (type === "seconds") {
      const target = Math.max(0, dur ? Math.min(dur, fractionOrSeconds) : fractionOrSeconds);
      video.currentTime = target;
    } else {
      // fraction
      const frac = Math.max(0, Math.min(0.999999, fractionOrSeconds));
      if (dur) {
        video.currentTime = frac * dur;
      }
    }
  }, [duration, getVideo]);

  const seekBy = useCallback((deltaSeconds: number) => {
    const next = Math.max(0, playedSeconds + deltaSeconds);
    seekTo(next, "seconds");
  }, [playedSeconds, seekTo]);

  const onTimeUpdate = useCallback((e: any) => {
    // Get the actual video element (works for both HLS custom elements and standard video)
    const video = e?.target ? getVideoFromElement(e.target) : getVideo();
    if (!video) return;
    const cur = video.currentTime || 0;
    const dur = isFinite(video.duration) && video.duration > 0 ? video.duration : duration;
    if (!seeking) {
      setPlayedSeconds(cur);
      if (dur > 0) setPlayed(Math.min(0.999999, cur / dur));
    }
  }, [seeking, duration, getVideo, getVideoFromElement]);

  const onProgress = useCallback((e: any) => {
    // Get the actual video element (works for both HLS custom elements and standard video)
    const video = e?.target ? getVideoFromElement(e.target) : getVideo();
    if (!video) return;
    try {
      if (video.buffered && video.buffered.length) {
        const end = video.buffered.end(video.buffered.length - 1);
        setLoadedSeconds(end);
      }
    } catch {
      // ignore DOMException on buffered ranges
    }
  }, [getVideo, getVideoFromElement]);

  const onDurationChange = useCallback((e: any) => {
    // Get the actual video element (works for both HLS custom elements and standard video)
    const video = e?.target ? getVideoFromElement(e.target) : getVideo();
    if (!video) return;
    if (isFinite(video.duration) && video.duration > 0) {
      setDuration(video.duration);
    }
  }, [getVideo, getVideoFromElement]);

  const onSeekMouseDown = useCallback(() => setSeeking(true), []);
  const onSeekChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setPlayed(value);
  }, []);
  const onSeekMouseUp = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setSeeking(false);
    seekTo(value, "fraction");
  }, [seekTo]);

  const toggleMute = useCallback(() => {
    setIsMuted(!isMuted);
  }, [isMuted]);

  const changeVolume = useCallback((delta: number) => {
    const newVolume = Math.max(0, Math.min(1, volume + delta));
    setVolume(newVolume);
    if (newVolume > 0 && isMuted) {
      setIsMuted(false);
    }
  }, [volume, isMuted]);

  const onVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (newVolume > 0 && isMuted) {
      setIsMuted(false);
    }
  }, [isMuted]);

  // Attach listeners to the underlying <video> to ensure time/duration updates on all browsers and react-player versions
  useEffect(() => {
    const video = getVideo();
    if (!video) return;

    const handleLoadedMetadata = () => {
      if (isFinite(video.duration)) setDuration(video.duration);
    };
    const handleDurationChange = () => {
      if (isFinite(video.duration)) setDuration(video.duration);
    };
    const handleTimeUpdate = () => {
      const cur = video.currentTime || 0;
      const dur = isFinite(video.duration) ? (video.duration || 0) : (duration || 0);
      setPlayedSeconds(cur);
      if (!seeking && dur > 0) setPlayed(Math.min(0.999999, cur / dur));
    };
    const handleProgress = () => {
      try {
        if (video.buffered && video.buffered.length) {
          const end = video.buffered.end(video.buffered.length - 1);
          setLoadedSeconds(end);
        }
      } catch {
        // ignore DOMException on buffered ranges
      }
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("durationchange", handleDurationChange);
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("progress", handleProgress);

    // Sync initial values
    handleLoadedMetadata();
    handleTimeUpdate();
    handleProgress();

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("durationchange", handleDurationChange);
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("progress", handleProgress);
    };
  }, [getVideo, duration, seeking]);

  // Mobile: toggle controls on tap. Desktop hover is handled purely via CSS :hover.
  const toggleControlsTouch = useCallback(() => {
    setShowControls(v => !v);
  }, []);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === " ") {
      e.preventDefault();
      togglePlay();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      seekBy(-5);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      seekBy(5);
    }
  }, [seekBy, togglePlay]);

  // no-op effect kept previously for timers; removed since we don't auto-hide anymore

  const currentTimeLabel = useMemo(() => formatTime(playedSeconds), [playedSeconds]);
  const durationLabel = useMemo(() => formatTime(duration), [duration]);

  return (
    <div className="stage">
      <div className="header">
        <img className="lockup" src="/brand/lockup-black.svg" alt="TurnPost logo and wordmark"/>
        <div className="tagline">
          Script-to-screen film & tv production platform <br/>
          <span className="light">powered by AI</span>
        </div>
      </div>
      <div className="panel">
        <div
          className={`video-box ${hasStarted ? "is-playing" : ""} ${showControls ? "show-controls" : ""}`}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onTouchStart={() => isPlaying && toggleControlsTouch()}
          ref={boxRef}
        >
          {!hasStarted && (
            <div className="play-overlay" onClick={onPlay}>
              <button aria-label="Play video">
                <img src="/player/play.svg" alt="Play"/>
              </button>
            </div>
          )}

          {hasStarted && (!isPlaying || hasEnded) && (
            <div className="pause-overlay" onClick={togglePlay}>
              <div className="pause-overlay-content">
                <h2 className="pause-heading">Let's Talk</h2>
                <p className="pause-subheading">If you're interested in being part of a revolution in scripted-content production, contact one of the people below!</p>
              </div>
            </div>
          )}
          <ReactPlayer
            className="video"
            src="https://d2k1av9qlpup3r.cloudfront.net/promo/h264/master.m3u8"
            playing={isPlaying}
            pip={false}
            playsInline={true}
            width="inherit"
            height="inherit"
            volume={volume}
            muted={isMuted}
            ref={playerRef}
            onReady={onReady}
            onTimeUpdate={onTimeUpdate}
            onProgress={onProgress}
            onDurationChange={onDurationChange}
            onEnded={onEnded}
          />

          {hasStarted && (
            <div className="controls-wrapper">
              <div className="controls-bar">
                <div className="volume-control">
                  <button
                    className="control volume-btn"
                    aria-label="Volume down"
                    onClick={() => changeVolume(-0.1)}
                    onMouseEnter={(e) => (e.currentTarget.querySelector('img')!.src = "/player/volume-lo.filled.svg")}
                    onMouseLeave={(e) => (e.currentTarget.querySelector('img')!.src = "/player/volume-lo.svg")}
                  >
                    <img src="/player/volume-lo.svg" alt="Volume down"/>
                  </button>

                  <input
                    type="range"
                    className="volume-slider"
                    min={0}
                    max={1}
                    step={0.01}
                    value={isMuted ? 0 : volume}
                    onChange={onVolumeChange}
                    aria-label="Volume"
                  />

                  <button
                    className="control volume-btn"
                    aria-label="Volume up"
                    onClick={() => changeVolume(0.1)}
                    onMouseEnter={(e) => (e.currentTarget.querySelector('img')!.src = "/player/volume-hi.filled.svg")}
                    onMouseLeave={(e) => (e.currentTarget.querySelector('img')!.src = "/player/volume-hi.svg")}
                  >
                    <img src="/player/volume-hi.svg" alt="Volume up"/>
                  </button>

                  <button
                    className="control volume-btn"
                    aria-label={isMuted ? "Unmute" : "Mute"}
                    onClick={toggleMute}
                    onMouseEnter={(e) => (e.currentTarget.querySelector('img')!.src = "/player/mute.filled.svg")}
                    onMouseLeave={(e) => (e.currentTarget.querySelector('img')!.src = "/player/mute.svg")}
                  >
                    <img src="/player/mute.svg" alt={isMuted ? "Unmuted" : "Mute"}/>
                  </button>
                </div>

                <div className="controls-center">
                  <button
                    className="control"
                    aria-label="Back 5 seconds"
                    onClick={() => seekBy(-5)}
                    onMouseEnter={(e) => (e.currentTarget.querySelector('img')!.src = "/player/back.filled.svg")}
                    onMouseLeave={(e) => (e.currentTarget.querySelector('img')!.src = "/player/back.svg")}
                  >
                    <img src="/player/back.svg" alt="Back"/>
                  </button>

                  <button
                    className="control primary"
                    aria-label={isPlaying ? "Pause" : "Play"}
                    onClick={togglePlay}
                    onMouseEnter={(e) => (e.currentTarget.querySelector('img')!.src = isPlaying ? "/player/pause.filled.svg" : "/player/play.filled.svg")}
                    onMouseLeave={(e) => (e.currentTarget.querySelector('img')!.src = isPlaying ? "/player/pause.svg" : "/player/play.svg")}
                  >
                    <img src={isPlaying ? "/player/pause.svg" : "/player/play.svg"} alt={isPlaying ? "Pause" : "Play"}/>
                  </button>

                  <button
                    className="control"
                    aria-label="Forward 5 seconds"
                    onClick={() => seekBy(5)}
                    onMouseEnter={(e) => (e.currentTarget.querySelector('img')!.src = "/player/forward.filled.svg")}
                    onMouseLeave={(e) => (e.currentTarget.querySelector('img')!.src = "/player/forward.svg")}
                  >
                    <img src="/player/forward.svg" alt="Forward"/>
                  </button>
                </div>

                <div className="timecodes" aria-hidden="true">
                  <span>{currentTimeLabel}</span>
                  <span> / </span>
                  <span>{durationLabel}</span>
                </div>
              </div>
              <div className="scrubber">
                <input
                  type="range"
                  min={0}
                  max={0.999999}
                  step={0.001}
                  value={seeking ? (played || 0) : (duration ? (played || 0) : 0)}
                  onMouseDown={onSeekMouseDown}
                  onChange={onSeekChange}
                  onMouseUp={onSeekMouseUp}
                  onTouchStart={onSeekMouseDown}
                  onTouchEnd={() => {
                    setSeeking(false);
                    seekTo(played, "fraction");
                  }}
                  aria-label="Seek"
                />
                {/* Buffered bar background via CSS variables */}
                <div className="buffer" style={{
                  ['--buffered' as any]: duration ? `${Math.min(100, (loadedSeconds / duration) * 100)}%` : '0%'
                }}/>
              </div>
            </div>
          )}
        </div>

        <div className="footer">
          <div className="contacts-list">
            <h2 className="contact-heading">Contact Us</h2>
            <Contact
              name="Kevin Wooten"
              title="CEO & Founder"
              emailAddress="kevin.wooten@turnpost.io"
            />
            <Contact
              name="Brett Baumayr"
              title="President & Founder"
              emailAddress="brett.baumayr@turnpost.io"
            />
            <Contact
              name="Darrell Rodriguez"
              title="Investor Relations"
              emailAddress="darrell.rodriguez@turnpost.io"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;

function formatTime(seconds: number) {
  if (!isFinite(seconds)) return "0:00";
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  const m = Math.floor((seconds / 60) % 60).toString();
  const h = Math.floor(seconds / 3600);
  return h > 0 ? `${h}:${m.padStart(2, "0")}:${s}` : `${m}:${s}`;
}
